/**
 * CHILD SESSION — which profile (Isaac / Bao / Papa) is signed in on THIS tablet
 * session, and the plumbing that tags every API call with it.
 *
 * The durable app state stays 100% SERVER-SIDE (multi-child design). This module only
 * holds the CURRENT identity for routing, and installs a one-time window.fetch wrapper
 * that stamps every same-origin `/api/` request with an `X-Child-Id` header so the
 * backend serves and persists the right child. Identity lives in sessionStorage: it
 * survives a page refresh (same "opening") but clears when the tab/app closes, so a
 * fresh launch always re-shows the profile selector (per the brainstorm: selector is
 * systematic at each opening, no persistent "who played last" on the device).
 */
export type ChildId = "isaac" | "bao";
export type Profile = ChildId | "papa";
// The id used for API routing. "papa" is the Papa profile's isolated TEST identity
// (its own server state) — active only while Pat is inside the Test tab, so his test
// runs never touch isaac's or bao's real sessions.
export type RoutingChildId = ChildId | "papa";

const KEY = "jarvis.profile";
const KEY_TEST = "jarvis.testmode"; // "1" while Papa's Test tab is open (session-scoped)

// Backend address, baked in at build time. Empty = same-origin (browser/dev, served
// by the server itself). For the Android build it's the VPS's absolute URL, e.g.
// https://62.238.44.119:8443 — the app loads its UI locally and calls this backend.
const API_BASE = process.env.REACT_APP_API_BASE ?? "";
// Device access token, baked into the APK. When set (and the server has DEVICE_TOKEN),
// it gates every backend call — the wall that replaces Tailscale once the VPS is public.
const DEVICE_TOKEN = process.env.REACT_APP_DEVICE_TOKEN ?? "";

export const CHILD_NAMES: Record<RoutingChildId, string> = { isaac: "Isaac", bao: "Bao", papa: "TEST" };

let current: Profile | null = readStored();
let testMode: boolean = readTest();

function readTest(): boolean {
  try {
    return sessionStorage.getItem(KEY_TEST) === "1";
  } catch {
    return false;
  }
}

function readStored(): Profile | null {
  try {
    const v = sessionStorage.getItem(KEY);
    return v === "isaac" || v === "bao" || v === "papa" ? (v as Profile) : null;
  } catch {
    return null;
  }
}

export function getProfile(): Profile | null {
  return current;
}

/**
 * The childId used for API routing. isaac/bao route to themselves; Papa routes to the
 * isolated "papa" test slot ONLY while the Test tab is open, else null (a plain Papa
 * dashboard call spans children and carries no single child id).
 */
export function getChildId(): RoutingChildId | null {
  if (current === "isaac" || current === "bao") return current;
  if (current === "papa" && testMode) return "papa";
  return null;
}

/** Enter/leave Papa's Test tab (routes API calls to the isolated "papa" slot). */
export function setTestMode(on: boolean): void {
  testMode = on;
  try {
    if (on) sessionStorage.setItem(KEY_TEST, "1");
    else sessionStorage.removeItem(KEY_TEST);
  } catch {
    /* memory-only fallback */
  }
  applyChildTheme();
}

export function isTestMode(): boolean {
  return testMode;
}

/** Applique le thème visuel de l'enfant courant (data-child sur <html>) : Bao =
 *  univers kawaii rose, Isaac/défaut = console cyan. Piloté par CSS (theme.scss). */
export function applyChildTheme(): void {
  if (typeof document === "undefined") return;
  const cid = getChildId(); // isaac | bao | papa | null
  document.documentElement.dataset.child = cid || "";
}
// Applique le thème de l'enfant mémorisé dès le chargement du module.
applyChildTheme();

export function setProfile(p: Profile): void {
  current = p;
  try {
    sessionStorage.setItem(KEY, p);
  } catch {
    /* private-mode / storage-off: identity just stays in memory for this load */
  }
  applyChildTheme();
}

export function clearProfile(): void {
  current = null;
  setTestMode(false); // leaving the profile always closes any open Test tab
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

// Install ONCE: wrap window.fetch so every same-origin /api/ request carries the
// current child's id. Non-/api/ and cross-origin requests pass through untouched.
let installed = false;
export function installChildHeader(): void {
  if (installed || typeof window === "undefined" || !window.fetch) return;
  installed = true;
  const orig = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (isApiRequest(input)) {
      const headers = new Headers(
        init?.headers || (input instanceof Request ? input.headers : undefined)
      );
      // Device token: the baked-in "wall" that replaces the Tailscale network wall
      // once the backend is exposed publicly. Sent on EVERY api call (incl. Papa's).
      if (DEVICE_TOKEN && !headers.has("X-Device-Token")) headers.set("X-Device-Token", DEVICE_TOKEN);
      // Child routing: only when a child (not Papa) is signed in.
      const cid = getChildId();
      if (cid && !headers.has("X-Child-Id")) headers.set("X-Child-Id", cid);
      // Redirect any relative /api/ call to the configured backend, so a stray call
      // site that used a relative path still reaches the VPS in the Android build
      // (where the app's own origin is https://localhost). Only string urls rewritten.
      let target: RequestInfo | URL = input;
      if (API_BASE && typeof input === "string" && input.startsWith("/api/")) {
        target = API_BASE + input;
      }
      return orig(target, { ...init, headers });
    }
    return orig(input, init);
  };
}

// A request is "an API call to OUR backend" if it targets the configured backend
// (API_BASE, absolute — the Android case, where the app origin is https://localhost
// and the backend is the VPS IP) OR a same-origin /api/ path (the browser/dev case).
function isApiRequest(input: RequestInfo | URL): boolean {
  try {
    const url = input instanceof Request ? input.url : String(input);
    if (API_BASE && url.startsWith(API_BASE + "/api")) return true;
    if (url.startsWith("/api/")) return true;
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}
